import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Languages, Wallet, Loader2, Camera, AlertTriangle, Mic, Square, Volume2, ArrowLeftRight } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { isVoiceSupported, listen, speak, VOICE_LANG_CODES } from "../lib/voice";

export default function Tools() {
  const [params] = useSearchParams();
  const [text, setText] = useState(params.get("text") || "");
  const [target, setTarget] = useState("Español");
  const [translation, setTranslation] = useState("");
  const [loadingT, setLoadingT] = useState(false);
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgError, setImgError] = useState("");
  const photoInputRef = useRef(null);

  const [amount, setAmount] = useState(1000);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("MXN");
  const [result, setResult] = useState(null);
  const [rates, setRates] = useState({});
  const [ratesError, setRatesError] = useState(false);

  const loadRates = () => {
    setRatesError(false);
    api.get("/currency/rates")
      .then(({ data }) => setRates(data.rates))
      // Sin esto, un fallo dejaba `rates` como {} para siempre — se veía
      // idéntico a "no hay monedas disponibles" en vez de un error real.
      .catch(() => setRatesError(true));
  };

  useEffect(() => { loadRates(); }, []);

  const translate = async () => {
    if (!text.trim()) return;
    setLoadingT(true);
    setTranslation("");
    try {
      const { data } = await api.post("/translate", { text, target_language: target });
      setTranslation(data.translation);
    } catch (e) {
      setTranslation("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingT(false);
    }
  };

  const translateImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgError("");
    setLoadingImg(true);
    setTranslation("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/translate-image?target_language=${encodeURIComponent(target)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setText("");
      setTranslation(data.translation);
    } catch (e) {
      setImgError(e?.response?.data?.detail || "No se pudo traducir la imagen");
    } finally {
      setLoadingImg(false);
    }
  };

  // Traductor de voz — reusa el mismo /translate y el mismo idioma
  // destino (`target`) que el traductor de texto de arriba; solo agrega
  // "en qué idioma hablo" y el ida-y-vuelta de escuchar/hablar.
  const [voiceFrom, setVoiceFrom] = useState("Español");
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceSpeaking, setVoiceSpeaking] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceTranslation, setVoiceTranslation] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const voiceHandleRef = useRef(null);
  const voiceSupported = isVoiceSupported();

  const swapVoiceLangs = () => {
    const prevFrom = voiceFrom;
    setVoiceFrom(target);
    setTarget(prevFrom);
  };

  const startVoiceListen = () => {
    setVoiceError("");
    setVoiceTranscript("");
    setVoiceTranslation("");
    setVoiceListening(true);
    voiceHandleRef.current = listen({
      lang: VOICE_LANG_CODES[voiceFrom] || "es-MX",
      onResult: async (transcript) => {
        setVoiceTranscript(transcript);
        setVoiceLoading(true);
        try {
          const { data } = await api.post("/translate", { text: transcript, target_language: target });
          setVoiceTranslation(data.translation);
          setVoiceSpeaking(true);
          speak({
            text: data.translation,
            lang: VOICE_LANG_CODES[target] || "en-US",
            onEnd: () => setVoiceSpeaking(false),
            onError: () => setVoiceSpeaking(false),
          });
        } catch (e) {
          setVoiceError(e?.response?.data?.detail || "No se pudo traducir lo que dijiste");
        } finally {
          setVoiceLoading(false);
        }
      },
      onError: (err) => {
        setVoiceListening(false);
        if (err === "not-allowed") {
          setVoiceError("Necesitas dar permiso de micrófono para usar el traductor de voz");
        } else if (err === "no-speech") {
          setVoiceError("No se escuchó nada, intenta de nuevo");
        } else {
          setVoiceError("No se pudo escuchar el micrófono");
        }
      },
      onEnd: () => setVoiceListening(false),
    });
  };

  const stopVoiceListen = () => {
    voiceHandleRef.current?.stop();
    setVoiceListening(false);
  };

  const replayVoice = () => {
    if (!voiceTranslation) return;
    setVoiceSpeaking(true);
    speak({
      text: voiceTranslation,
      lang: VOICE_LANG_CODES[target] || "en-US",
      onEnd: () => setVoiceSpeaking(false),
      onError: () => setVoiceSpeaking(false),
    });
  };

  const convert = async () => {
    try {
      const { data } = await api.post("/currency/convert", {
        amount: parseFloat(amount),
        from_currency: from,
        to_currency: to,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Error" });
    }
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 space-y-12">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Herramientas</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Traducción & <span className="text-[var(--brand-cyan,#FF85AC)]">Conversor</span>
          </h1>
        </div>

        {/* Translator */}
        <article className="glass rounded-3xl p-8 border border-white/10 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <Languages className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Traducción IA</h2>
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Pega aquí la nota del proveedor en cualquier idioma…"
            data-testid="translate-input"
            rows={4}
            className="w-full bg-black/40 rounded-2xl p-4 text-sm text-white border border-white/10 focus:border-[var(--brand-cyan,#FF85AC)]/50 outline-none transition-colors"
          />

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="target-language-select"
              className="bg-black border border-white/15 rounded-full px-4 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
            >
              {["Español", "English", "Français", "Português", "Italiano", "Deutsch", "日本語"].map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
            <button
              onClick={translate}
              disabled={loadingT}
              data-testid="translate-btn"
              className="bg-[var(--brand-cyan,#FF85AC)] text-black px-6 py-2.5 rounded-full font-semibold hover:bg-[#FF9DC0] transition-all disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loadingT && <Loader2 className="w-4 h-4 animate-spin" />}
              Traducir
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={translateImage}
              className="hidden"
              data-testid="translate-photo-input"
            />
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={loadingImg}
              data-testid="translate-photo-btn"
              className="border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 text-white px-6 py-2.5 rounded-full font-semibold transition-all disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              Traducir foto
            </button>
          </div>
          {imgError && <p className="mt-3 text-sm text-[#FF3B30]" data-testid="translate-photo-error">{imgError}</p>}

          {translation && (
            <div className="mt-5 p-5 rounded-2xl bg-[var(--brand-cyan,#FF85AC)]/5 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid="translate-output">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-2">Resultado</p>
              <p className="text-sm leading-relaxed text-neutral-100">{translation}</p>
            </div>
          )}
        </article>

        {/* Voice translator */}
        <article className="glass rounded-3xl p-8 border border-white/10 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <Mic className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Traductor de Voz</h2>
          </div>

          {!voiceSupported ? (
            <p className="text-sm text-neutral-400" data-testid="voice-unsupported">
              Tu navegador no soporta reconocimiento de voz. Prueba en Chrome
              (Android o computadora).
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <select
                  value={voiceFrom}
                  onChange={(e) => setVoiceFrom(e.target.value)}
                  disabled={voiceListening}
                  data-testid="voice-from-select"
                  className="bg-black border border-white/15 rounded-full px-4 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none disabled:opacity-50"
                >
                  {Object.keys(VOICE_LANG_CODES).map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={swapVoiceLangs}
                  disabled={voiceListening}
                  title="Intercambiar idiomas"
                  className="p-2 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 transition-colors disabled:opacity-50"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </button>
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  disabled={voiceListening}
                  data-testid="voice-to-select"
                  className="bg-black border border-white/15 rounded-full px-4 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none disabled:opacity-50"
                >
                  {Object.keys(VOICE_LANG_CODES).map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={voiceListening ? stopVoiceListen : startVoiceListen}
                  disabled={voiceLoading}
                  data-testid="voice-mic-btn"
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all disabled:opacity-50 ${
                    voiceListening
                      ? "bg-[#FF3B30] animate-pulse"
                      : "bg-[var(--brand-cyan,#FF85AC)] hover:bg-[#FF9DC0]"
                  }`}
                >
                  {voiceLoading ? (
                    <Loader2 className="w-8 h-8 text-black animate-spin" />
                  ) : voiceListening ? (
                    <Square className="w-7 h-7 text-black" />
                  ) : (
                    <Mic className="w-8 h-8 text-black" />
                  )}
                </button>
              </div>
              <p className="text-center text-xs text-neutral-500 mt-3">
                {voiceListening ? "Escuchando… toca para detener" : voiceLoading ? "Traduciendo…" : "Toca y habla"}
              </p>

              {voiceError && (
                <p className="mt-4 text-sm text-[#FF3B30] text-center" data-testid="voice-error">{voiceError}</p>
              )}

              {voiceTranscript && (
                <div className="mt-5 p-4 rounded-2xl bg-black/40 border border-white/10" data-testid="voice-transcript">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Dijiste ({voiceFrom})</p>
                  <p className="text-sm text-neutral-300">{voiceTranscript}</p>
                </div>
              )}

              {voiceTranslation && (
                <div className="mt-3 p-5 rounded-2xl bg-[var(--brand-cyan,#FF85AC)]/5 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid="voice-output">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">Traducción ({target})</p>
                    <button
                      type="button"
                      onClick={replayVoice}
                      disabled={voiceSpeaking}
                      title="Repetir en voz alta"
                      className="text-[var(--brand-cyan,#FF85AC)] disabled:opacity-40"
                    >
                      <Volume2 className={`w-4 h-4 ${voiceSpeaking ? "animate-pulse" : ""}`} />
                    </button>
                  </div>
                  <p className="text-sm leading-relaxed text-neutral-100">{voiceTranslation}</p>
                </div>
              )}
            </>
          )}
        </article>

        {/* Currency */}
        <article className="glass rounded-3xl p-8 border border-white/10 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <Wallet className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Conversor de Moneda</h2>
          </div>

          {ratesError && (
            <div className="mb-5 flex items-center justify-between gap-3 p-4 rounded-2xl bg-[#FFB020]/5 border border-[#FFB020]/30" data-testid="rates-error">
              <span className="flex items-center gap-2 text-sm text-[#FFB020]">
                <AlertTriangle className="w-4 h-4 shrink-0" /> No se pudieron cargar los tipos de cambio.
              </span>
              <button onClick={loadRates} className="text-xs uppercase tracking-[0.2em] text-[#FFB020] hover:underline shrink-0">
                Reintentar
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_1fr_auto] sm:items-end gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Monto</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="amount-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">De</label>
              <select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="from-select"
                className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
              >
                {Object.keys(rates).map((k) => <option key={k}>{k}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={() => { setFrom(to); setTo(from); }}
              title="Intercambiar monedas"
              data-testid="swap-currency-btn"
              className="p-2 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 transition-colors"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">A</label>
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="to-select"
                className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
              >
                {Object.keys(rates).map((k) => <option key={k}>{k}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={convert}
                disabled={Object.keys(rates).length === 0}
                data-testid="convert-btn"
                className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-2.5 rounded-full font-semibold hover:bg-[#FF9DC0] disabled:opacity-50"
              >
                Convertir
              </button>
            </div>
          </div>

          {result && !result.error && (
            <div className="mt-5 p-5 rounded-2xl bg-[var(--brand-cyan,#FF85AC)]/5 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid="convert-output">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-1">
                {result.amount} {result.from} =
              </p>
              <p className="font-display text-3xl text-white">
                {result.result.toLocaleString()} <span className="text-base text-neutral-400">{result.to}</span>
              </p>
              <p className="text-xs text-neutral-500 mt-2">1 {result.from} = {result.rate} {result.to}</p>
            </div>
          )}
        </article>
      </section>
    </Layout>
  );
}
